/**
 * CaptureConfigDetailPage - Erfassungsregeln bearbeiten
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Settings, ArrowLeft, RefreshCw, Save, Trash2, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface LaundryClassEntity {
  id: string;
  code: string;
  name: string;
  // NEU: Pro-LC Artikelgruppen-Regeln
  requiresArticleGroup?: boolean;
  allowedArticleGroups?: ArticleGroupEntity[];
}

// NEU: Pro-LC Einstellungen State
interface PerLcSettings {
  requiresArticleGroup: boolean;
  allowedArticleGroupIds: string[];
}

interface ArticleGroupEntity {
  id: string;
  code: string;
  name: string;
}

interface CaptureConfig {
  id: string;
  screen: 'FL' | 'WSA';
  lineId: string | null;
  lineName: string | null;
  requiresLaundryClass: boolean;
  requiresArticleGroup: boolean;
  allowedLaundryClasses: LaundryClassEntity[];
  allowedArticleGroups: ArticleGroupEntity[];
}

// Default: Pro-LC Artikelgruppen-Pflicht aktiviert (wie global)
const DEFAULT_PER_LC_SETTINGS: PerLcSettings = {
  requiresArticleGroup: true,
  allowedArticleGroupIds: [],
};

interface SubLine {
  id: string;
  name: string;
}

interface Line {
  id: string;
  name: string;
  subLines?: SubLine[];
}

const SCREEN_LABELS: Record<'FL' | 'WSA', string> = {
  FL: 'Vorarbeiter (FL)',
  WSA: 'Waschstation (WSA)',
};

export default function CaptureConfigDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [config, setConfig] = useState<CaptureConfig | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [allLaundryClasses, setAllLaundryClasses] = useState<LaundryClassEntity[]>([]);
  const [allArticleGroups, setAllArticleGroups] = useState<ArticleGroupEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State - Wäscheklasse
  const [screen, setScreen] = useState<'FL' | 'WSA'>('FL');
  const [lineId, setLineId] = useState<string>('');
  const [requiresLaundryClass, setRequiresLaundryClass] = useState(false);
  const [allowAll, setAllowAll] = useState(true);
  const [allowedLaundryClassIds, setAllowedLaundryClassIds] = useState<string[]>([]);

  // Form State - Artikelgruppe (global)
  const [requiresArticleGroup, setRequiresArticleGroup] = useState(false);
  const [allowAllGroups, setAllowAllGroups] = useState(true);
  const [allowedArticleGroupIds, setAllowedArticleGroupIds] = useState<string[]>([]);

  // NEU: Form State - Pro-LC Artikelgruppen-Regeln
  // Map: laundryClassId -> { requiresArticleGroup, allowedArticleGroupIds }
  const [perLcSettings, setPerLcSettings] = useState<Record<string, PerLcSettings>>({});
  // Welche LC hat erweiterte Einstellungen offen?
  const [expandedLcId, setExpandedLcId] = useState<string | null>(null);

  // Delete Confirm State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load data
  const loadData = async () => {
    setLoading(true);
    try {
      const [linesRes, lcRes, agRes] = await Promise.all([
        fetch('/api/data/lines', { credentials: 'include' }),
        fetch('/api/data/laundry-classes', { credentials: 'include' }),
        fetch('/api/data/article-groups', { credentials: 'include' }),
      ]);

      if (linesRes.ok) {
        setLines(await linesRes.json());
      }
      if (lcRes.ok) {
        setAllLaundryClasses(await lcRes.json());
      }
      if (agRes.ok) {
        setAllArticleGroups(await agRes.json());
      }

      if (!isNew && id) {
        const configRes = await fetch(`/api/admin/capture-configs`, { credentials: 'include' });
        if (configRes.ok) {
          const data = await configRes.json();
          const found = (data.configs || []).find((c: CaptureConfig) => c.id === id);
          if (found) {
            setConfig(found);
            setScreen(found.screen);
            setLineId(found.lineId || '');
            setRequiresLaundryClass(found.requiresLaundryClass);
            setRequiresArticleGroup(found.requiresArticleGroup);
            setAllowAll(found.allowedLaundryClasses.length === 0);
            setAllowedLaundryClassIds(found.allowedLaundryClasses.map((lc: LaundryClassEntity) => lc.id));
            setAllowAllGroups(found.allowedArticleGroups.length === 0);
            setAllowedArticleGroupIds(found.allowedArticleGroups.map((ag: ArticleGroupEntity) => ag.id));
            
            // NEU: Pro-LC Einstellungen laden
            const perLc: Record<string, PerLcSettings> = {};
            for (const lc of found.allowedLaundryClasses) {
              perLc[lc.id] = {
                requiresArticleGroup: lc.requiresArticleGroup ?? true,
                allowedArticleGroupIds: (lc.allowedArticleGroups || []).map((ag: ArticleGroupEntity) => ag.id),
              };
            }
            setPerLcSettings(perLc);
          } else {
            navigate('/admin/capture-config');
          }
        }
      }
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [id, isNew]);

  // Save
  const handleSave = async () => {
    // Validierung Wäscheklasse
    if (requiresLaundryClass && !allowAll && allowedLaundryClassIds.length === 0) {
      setError('Bitte mindestens eine Wäscheklasse auswählen oder "Alle erlauben" aktivieren');
      return;
    }
    // Validierung Artikelgruppe
    if (requiresArticleGroup && !allowAllGroups && allowedArticleGroupIds.length === 0) {
      setError('Bitte mindestens eine Artikelgruppe auswählen oder "Alle erlauben" aktivieren');
      return;
    }

    setSaving(true);
    setError(null);

    const allowedLcIds = allowAll ? [] : allowedLaundryClassIds;
    const allowedAgIds = allowAllGroups ? [] : allowedArticleGroupIds;
    
    // NEU: Pro-LC Einstellungen für API vorbereiten
    const perLcSettingsPayload = allowedLcIds.map(lcId => ({
      laundryClassId: lcId,
      requiresArticleGroup: perLcSettings[lcId]?.requiresArticleGroup ?? true,
      allowedArticleGroupIds: perLcSettings[lcId]?.allowedArticleGroupIds ?? [],
    }));

    try {
      if (isNew) {
        const res = await fetch('/api/admin/capture-configs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            screen,
            lineId: lineId || undefined,
            requiresLaundryClass,
            requiresArticleGroup,
            allowedLaundryClassIds: allowedLcIds,
            allowedArticleGroupIds: allowedAgIds,
            perLcSettings: perLcSettingsPayload, // NEU
          }),
        });

        if (res.ok) {
          const newConfig = await res.json();
          navigate(`/admin/capture-config/${newConfig.id}`);
        } else {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || 'Fehler beim Anlegen');
        }
      } else {
        const res = await fetch(`/api/admin/capture-configs/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            requiresLaundryClass,
            requiresArticleGroup,
            allowedLaundryClassIds: allowedLcIds,
            allowedArticleGroupIds: allowedAgIds,
            perLcSettings: perLcSettingsPayload, // NEU
          }),
        });

        if (res.ok) {
          navigate('/admin/capture-config');
        } else {
          const data = await res.json().catch(() => ({}));
          setError(data.error || data.message || 'Fehler beim Speichern');
        }
      }
    } catch (err) {
      setError('Netzwerkfehler');
    }
    setSaving(false);
  };

  // Delete
  const handleDelete = async () => {
    if (!id || isNew) return;

    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/capture-configs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        navigate('/admin/capture-config');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Fehler beim Löschen');
        setDeleteConfirmOpen(false);
      }
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
    setDeleting(false);
  };

  // Toggle Wäscheklasse
  const toggleLaundryClass = (lcId: string) => {
    const wasSelected = allowedLaundryClassIds.includes(lcId);
    setAllowedLaundryClassIds(prev =>
      wasSelected
        ? prev.filter(x => x !== lcId)
        : [...prev, lcId]
    );
    // NEU: Bei Hinzufügen Default-Einstellungen setzen
    if (!wasSelected) {
      setPerLcSettings(prev => ({
        ...prev,
        [lcId]: prev[lcId] || { ...DEFAULT_PER_LC_SETTINGS },
      }));
    }
  };

  // NEU: Pro-LC requiresArticleGroup ändern
  const setPerLcRequiresAG = (lcId: string, value: boolean) => {
    setPerLcSettings(prev => ({
      ...prev,
      [lcId]: { ...(prev[lcId] || DEFAULT_PER_LC_SETTINGS), requiresArticleGroup: value },
    }));
  };

  // NEU: Pro-LC allowedArticleGroups ändern
  const togglePerLcArticleGroup = (lcId: string, agId: string) => {
    setPerLcSettings(prev => {
      const current = prev[lcId] || DEFAULT_PER_LC_SETTINGS;
      const wasSelected = current.allowedArticleGroupIds.includes(agId);
      return {
        ...prev,
        [lcId]: {
          ...current,
          allowedArticleGroupIds: wasSelected
            ? current.allowedArticleGroupIds.filter(x => x !== agId)
            : [...current.allowedArticleGroupIds, agId],
        },
      };
    });
  };

  // Display title
  const getTitle = (): string => {
    if (isNew) return 'Neue Regel anlegen';
    return 'Erfassungsregeln bearbeiten';
  };

  // Aktuelle Strecke mit SubLines
  const selectedLine = lines.find(l => l.id === (config?.lineId || lineId));
  const hasSubLines = (selectedLine?.subLines?.length ?? 0) > 0;

  // Gültigkeitstext
  const getValidityText = (): string => {
    if (screen === 'WSA') return 'Waschstation (WSA)';
    const lineName = config?.lineName || selectedLine?.name;
    if (!lineName) return 'Alle Strecken (Global)';
    return lineName + (hasSubLines ? ' (alle Varianten)' : '');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  if (!isNew && !config) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Regel nicht gefunden</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/capture-config"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {getTitle()}
            </h1>
          </div>
          <div className="flex gap-2">
            {!isNew && (
              <button
                onClick={() => loadData()}
                disabled={loading}
                className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                title="Aktualisieren"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || (requiresLaundryClass && !allowAll && allowedLaundryClassIds.length === 0)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Gültigkeit - nur bei Edit oder wenn Strecke gewählt */}
        {(!isNew || lineId || screen === 'WSA') && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <div>
              <span className="text-sm font-medium text-emerald-800">Diese Regeln gelten für: </span>
              <span className="text-sm text-emerald-700">{getValidityText()}</span>
            </div>
          </div>
        )}

        {/* Substrecken-Hinweis */}
        {hasSubLines && screen === 'FL' && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p>
                Diese Strecke hat Varianten. Der Vorarbeiter muss zuerst eine Variante auswählen.
                Danach gelten die unten definierten Erfassungsregeln.
              </p>
            </div>
          </div>
        )}

        {/* Grundeinstellungen (nur Create) */}
        {isNew && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Geltungsbereich</h2>

            {/* Screen */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Erfassungsbereich <span className="text-red-500">*</span>
              </label>
              <select
                value={screen}
                onChange={(e) => setScreen(e.target.value as 'FL' | 'WSA')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="FL">Vorarbeiter</option>
                <option value="WSA">Waschstation</option>
              </select>
            </div>

            {/* Strecke (nur FL) */}
            {screen === 'FL' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Strecke
                </label>
                <select
                  value={lineId}
                  onChange={(e) => setLineId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Alle Strecken (Global)</option>
                  {lines.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Strecken-spezifische Regeln haben Vorrang vor globalen Regeln
                </p>
              </div>
            )}
          </section>
        )}

        {/* Erfassungsregeln */}
        <section className="bg-white rounded-lg border p-6 space-y-6">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">
            Erfassungsregeln
          </h2>

          {/* Wäscheklasse */}
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresLaundryClass}
                onChange={(e) => setRequiresLaundryClass(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-base font-medium text-slate-900">Wäscheklasse muss erfasst werden</span>
                <p className="text-sm text-slate-500 mt-0.5">
                  Der Vorarbeiter muss bei jeder Erfassung eine Wäscheklasse auswählen
                </p>
              </div>
            </label>

            {/* Einschränkung (nur wenn Pflicht aktiv) */}
            {requiresLaundryClass && (
              <div className="ml-8 pl-4 border-l-2 border-slate-200 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="laundryClassRestriction"
                    checked={allowAll}
                    onChange={() => setAllowAll(true)}
                    className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Alle Wäscheklassen zulässig</span>
                </label>

                {/* NEU: Pro-LC Einstellungen auch bei "Alle zulässig" */}
                {allowAll && allLaundryClasses.length > 0 && (
                  <div className="ml-7 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600 mb-2 font-medium">Pro-Wäscheklasse Artikelgruppen-Regeln (optional):</p>
                    <div className="space-y-2">
                      {allLaundryClasses.map(lc => {
                        const isExpanded = expandedLcId === lc.id;
                        const lcSettings = perLcSettings[lc.id] || DEFAULT_PER_LC_SETTINGS;
                        
                        return (
                          <div key={lc.id} className="border rounded bg-white">
                            <button
                              type="button"
                              onClick={() => {
                                setExpandedLcId(isExpanded ? null : lc.id);
                                if (!perLcSettings[lc.id]) {
                                  setPerLcSettings(prev => ({ ...prev, [lc.id]: { ...DEFAULT_PER_LC_SETTINGS } }));
                                }
                              }}
                              className="w-full flex items-center justify-between p-2 text-left hover:bg-slate-50"
                            >
                              <span className="text-sm text-slate-700">{lc.name}</span>
                              <span className="text-xs text-blue-600">{isExpanded ? '▲' : '▼'}</span>
                            </button>
                            
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-slate-100">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={lcSettings.requiresArticleGroup}
                                    onChange={(e) => setPerLcRequiresAG(lc.id, e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-xs text-slate-700">Artikelgruppe Pflicht</span>
                                  {!lcSettings.requiresArticleGroup && (
                                    <span className="text-xs text-amber-600 font-medium">(überschreibt global)</span>
                                  )}
                                </label>
                                
                                {lcSettings.requiresArticleGroup && (
                                  <div className="ml-6 mt-2">
                                    <p className="text-xs text-slate-500 mb-1">Erlaubte Artikelgruppen:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {allArticleGroups.map(ag => (
                                        <button
                                          key={ag.id}
                                          type="button"
                                          onClick={() => togglePerLcArticleGroup(lc.id, ag.id)}
                                          className={`text-xs px-2 py-1 rounded border ${
                                            lcSettings.allowedArticleGroupIds.includes(ag.id)
                                              ? 'bg-blue-100 border-blue-300 text-blue-800'
                                              : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                          }`}
                                        >
                                          {ag.name}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="laundryClassRestriction"
                    checked={!allowAll}
                    onChange={() => setAllowAll(false)}
                    className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Nur folgende Wäscheklassen zulässig:</span>
                </label>

                {/* Wäscheklassen-Auswahl mit pro-LC Einstellungen */}
                {!allowAll && (
                  <div className="ml-7 space-y-3">
                    {allLaundryClasses.length === 0 ? (
                      <p className="text-sm text-slate-500">Keine Wäscheklassen vorhanden</p>
                    ) : (
                      allLaundryClasses.map(lc => {
                        const isSelected = allowedLaundryClassIds.includes(lc.id);
                        const isExpanded = expandedLcId === lc.id;
                        const lcSettings = perLcSettings[lc.id] || DEFAULT_PER_LC_SETTINGS;
                        
                        return (
                          <div key={lc.id} className={`border rounded-lg ${isSelected ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between p-2">
                              <label className="flex items-center gap-3 cursor-pointer flex-1">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleLaundryClass(lc.id)}
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700 font-medium">{lc.name}</span>
                              </label>
                              {isSelected && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedLcId(isExpanded ? null : lc.id)}
                                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1"
                                >
                                  {isExpanded ? '▲ Weniger' : '▼ AG-Regeln'}
                                </button>
                              )}
                            </div>
                            
                            {/* Pro-LC Artikelgruppen-Einstellungen (aufklappbar) */}
                            {isSelected && isExpanded && (
                              <div className="px-3 pb-3 pt-1 border-t border-slate-200 bg-white rounded-b-lg">
                                <div className="space-y-2">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={lcSettings.requiresArticleGroup}
                                      onChange={(e) => setPerLcRequiresAG(lc.id, e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-xs text-slate-700">Artikelgruppe Pflicht</span>
                                    {!lcSettings.requiresArticleGroup && (
                                      <span className="text-xs text-amber-600 font-medium">(überschreibt global)</span>
                                    )}
                                  </label>
                                  
                                  {lcSettings.requiresArticleGroup && (
                                    <div className="ml-6 mt-1">
                                      <p className="text-xs text-slate-500 mb-1">Erlaubte Artikelgruppen (leer = alle aus global):</p>
                                      <div className="flex flex-wrap gap-1">
                                        {allArticleGroups.map(ag => (
                                          <button
                                            key={ag.id}
                                            type="button"
                                            onClick={() => togglePerLcArticleGroup(lc.id, ag.id)}
                                            className={`text-xs px-2 py-1 rounded border ${
                                              lcSettings.allowedArticleGroupIds.includes(ag.id)
                                                ? 'bg-blue-100 border-blue-300 text-blue-800'
                                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                            }`}
                                          >
                                            {ag.name}
                                          </button>
                                        ))}
                                      </div>
                                      {lcSettings.allowedArticleGroupIds.length > 0 && (
                                        <p className="text-xs text-slate-400 mt-1">
                                          {lcSettings.allowedArticleGroupIds.length} ausgewählt (Intersection mit global)
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    {allowedLaundryClassIds.length === 0 && (
                      <p className="text-xs text-red-500 mt-2">
                        Mindestens eine Wäscheklasse auswählen
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Artikelgruppe */}
          <div className="pt-4 border-t border-slate-200">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresArticleGroup}
                onChange={(e) => setRequiresArticleGroup(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-slate-900">Artikelgruppe muss erfasst werden</span>
                <p className="text-sm text-slate-500">Der Vorarbeiter muss eine Artikelgruppe auswählen</p>
              </div>
            </label>

            {requiresArticleGroup && (
              <div className="mt-3 ml-8 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={allowAllGroups}
                    onChange={() => setAllowAllGroups(true)}
                    className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Alle Artikelgruppen zulässig</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!allowAllGroups}
                    onChange={() => setAllowAllGroups(false)}
                    className="w-4 h-4 border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">Nur folgende Artikelgruppen zulässig:</span>
                </label>

                {!allowAllGroups && (
                  <div className="ml-6 mt-2 space-y-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {allArticleGroups.length === 0 ? (
                      <p className="text-sm text-slate-500">Keine Artikelgruppen verfügbar</p>
                    ) : (
                      allArticleGroups.map((ag) => (
                        <label key={ag.id} className="flex items-center gap-2 cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={allowedArticleGroupIds.includes(ag.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAllowedArticleGroupIds([...allowedArticleGroupIds, ag.id]);
                              } else {
                                setAllowedArticleGroupIds(allowedArticleGroupIds.filter(gid => gid !== ag.id));
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">{ag.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}

                {!allowAllGroups && allowedArticleGroupIds.length === 0 && (
                  <p className="text-xs text-red-500 mt-2">
                    Mindestens eine Artikelgruppe auswählen
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Löschen (nur bei Edit) */}
        {!isNew && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Regel dauerhaft löschen</h2>
            <p className="text-sm text-slate-600 mb-4">
              Diese Regel wird unwiderruflich gelöscht und kann nicht wiederhergestellt werden.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Dauerhaft löschen
            </button>
          </section>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Regel dauerhaft löschen?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                Diese Regel wird unwiderruflich gelöscht und kann nicht wiederhergestellt werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
